import { useEffect, useRef, useState } from 'react';
import { Sidebar } from './Sidebar';
import { useAppStore } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import { AnnouncementPopup } from './AnnouncementPopup';
import { RegionPrompt } from './RegionPrompt';
import { PaymentAlertBanner } from './PaymentAlertBanner';
import { SupportSessionBanner } from './support/SupportSessionBanner';
import { Loader2, Menu } from 'lucide-react';
import { useSidebarStore } from '../store/sidebarStore';
import { DemoBar, DemoBarSpacer } from './demo/DemoBar';
import { useDemoSession } from './demo/useDemoSession';
import { PhoneHeaderSlot, PHONE_HEADER_HEIGHT } from './PhoneHeader';
import { useIsPhone } from '../hooks/useIsPhone';

export function Layout({ children }) {
  const { settings, settingsLoading, fetchSettings } = useAppStore();
  const { user, isAuthenticated, sessionToken } = useAuthStore();
  // The sign-in settings were last asked for. A failed load leaves settings
  // empty and flips settingsLoading, which re-ran this effect: after logging
  // out it asked again and again, fast enough to bog the browser down.
  const askedFor = useRef(null);
  const demo = useDemoSession();
  const isPhone = useIsPhone();
  // The phone bar's title slot, once mounted (see PhoneHeader)
  const [titleSlot, setTitleSlot] = useState(null);

  useEffect(() => {
    // Ensure settings are loaded for regional formatting: once per sign-in
    if (!settings && !settingsLoading && isAuthenticated && askedFor.current !== sessionToken) {
      askedFor.current = sessionToken;
      fetchSettings();
    }
  }, [settings, settingsLoading, fetchSettings, isAuthenticated, sessionToken]);

  // Show loading only on first load when no settings cached
  const showLoading = settingsLoading && !settings && !localStorage.getItem('user_region');

  if (showLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar demo={demo} />
      <main className="flex-1 min-w-0 min-h-screen overflow-x-clip">
        {/* 16px gutter on phones; the design system's 48/32 padding on desktop, capped at page-max */}
        <div className="px-4 py-6 sm:p-6 lg:px-8 lg:py-12 max-w-[1520px]">
          {/* Phone: a sticky bar, menu on the left and the page's title on the
              right (PhoneTitle). -mt-6 takes the page's top padding, so the
              bar starts at the top of the screen */}
          {isPhone && (
            <div
              className="sticky top-0 z-40 -mx-4 -mt-6 mb-6 px-4 bg-background flex items-center gap-3"
              style={{ height: PHONE_HEADER_HEIGHT }}
              data-testid="phone-header"
            >
              <MenuButton inBar />
              <div ref={setTitleSlot} className="flex-1 min-w-0 flex items-center gap-3" />
            </div>
          )}
          <SupportSessionBanner />
          <PaymentAlertBanner />
          {!isPhone && <MenuButton />}
          <PhoneHeaderSlot.Provider value={isPhone ? titleSlot : null}>
            {children}
          </PhoneHeaderSlot.Provider>
          {demo.isDemo && <DemoBarSpacer />}
        </div>
      </main>
      {demo.isDemo && <DemoBar minutesLeft={demo.minutesLeft} onSignUp={() => demo.signUp('demo_bar')} />}
      <AnnouncementPopup user={user} />
      <RegionPrompt />
    </div>
  );
}

/**
 * Opens the sidebar drawer on a phone or tablet. It floats at the start of
 * the page, so whatever each page puts first - its title, or a back button
 * before the title - sits beside it rather than under it, and the content
 * below flows at full width. Styled like the pages' back buttons.
 */
function MenuButton({ inBar = false }) {
  const setDrawerOpen = useSidebarStore((s) => s.setDrawerOpen);
  return (
    <button
      type="button"
      onClick={() => setDrawerOpen(true)}
      aria-label="Open menu"
      className={`lg:hidden ${inBar ? 'shrink-0' : 'float-left mr-3 menu-button-offset'} flex items-center justify-center w-10 h-10 rounded-full bg-mist hover:bg-parchment transition-colors`}
      data-testid="mobile-menu-btn"
    >
      <Menu className="w-[18px] h-[18px]" />
    </button>
  );
}
